import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Transition, Dialog, Menu } from '@headlessui/react';
import { Fragment } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, DocumentArrowDownIcon, TruckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import CreateDelivery from '../components/CreateDelivery';

function KanbanJob() {
  const { user } = useAuth();
  const { id } = useParams();
  const [kanbanJobs, setKanbanJobs] = useState([]);
  const [poNumber, setPoNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [isCreateWorkOrderModalOpen, setIsCreateWorkOrderModalOpen] = useState(false);
  const [isLogProductionModalOpen, setIsLogProductionModalOpen] = useState(false);
  const [isLogShipmentModalOpen, setIsLogShipmentModalOpen] = useState(false);
  const [isCreateDeliveryModalOpen, setIsCreateDeliveryModalOpen] = useState(false);
  const [isNewDeliveryModalOpen, setIsNewDeliveryModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [newWorkOrderLineItem, setNewWorkOrderLineItem] = useState(null);
  const [newQuantityOrdered, setNewQuantityOrdered] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [productionQuantity, setProductionQuantity] = useState('');
  const [shipmentQuantity, setShipmentQuantity] = useState('');
  const [deliveryQuantity, setDeliveryQuantity] = useState('');
  const [packageNumber, setPackageNumber] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [files, setFiles] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  

  const [isPdfPreviewModalOpen, setIsPdfPreviewModalOpen] = useState(false);
const [pdfUrl, setPdfUrl] = useState(null);
const [isEditPoModalOpen, setIsEditPoModalOpen] = useState(false);
const [selectedKanbanJob, setSelectedKanbanJob] = useState(null);
const [editedPoNumber, setEditedPoNumber] = useState('');
const [editedLineItems, setEditedLineItems] = useState([]);
const [newLineItem, setNewLineItem] = useState({
  part_number: '',
  description: '',
  quantity_on_order: '',
  shop_id: '',
});
const [shops, setShops] = useState([]);


const handlePreviewPDF = async (filePath, bucket = 'delivery-files') => {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 60);
      if (error) throw new Error(`Failed to generate signed URL: ${error.message}`);
  
      // Open the PDF in a new tab
      window.open(data.signedUrl, '_blank');
  
      // Refresh the current tab
      window.location.reload();
    } catch (err) {
      console.error('Error generating PDF preview URL:', err);
      toast.error(`Failed to load PDF preview: ${err.message}`);
    }
  };
  

const closePdfPreviewModal = () => {
  setIsPdfPreviewModalOpen(false);
  setPdfUrl(null);
};

useEffect(() => {
    const fetchShops = async () => {
      try {
        const { data, error } = await supabase
          .from('shops')
          .select('id, name')
          .order('name');
        if (error) throw error;
        setShops(data || []);
      } catch (err) {
        console.error('Error fetching shops:', err.message);
        toast.error(`Failed to fetch shops: ${err.message}`);
      }
    };
  
    fetchShops();
  }, []);

  useEffect(() => {
    document.title = `Kanban PO #${poNumber || ''}`;
  }, [poNumber]);


 useEffect(() => {
  const fetchKanbanJob = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: jobData, error: jobError } = await supabase
        .from('kanban_jobs')
        .select('po_number')
        .eq('id', id)
        .single();
      if (jobError) throw jobError;

      if (!jobData) throw new Error('Kanban job not found');
      setPoNumber(jobData.po_number);

      const { data: jobsData, error: jobsError } = await supabase
        .from('kanban_jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status, 
          quantity_on_order, produced_quantity, shipped_quantity, 
          po_start_date, unit_price, 
          shops(id, name), customers(id, nickname, name, 
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone, net_terms),
          parts!inner(part_number, part_number_master, revision, inventory_quantity)
        `)
        .eq('po_number', jobData.po_number)
        .order('po_line_number');
      if (jobsError) throw jobsError;

      const formattedJobs = jobsData.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        part_number_master: job.parts?.part_number_master || 'N/A',
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        po_start_date: job.po_start_date,
        unit_price: job.unit_price || 0,
        shop_name: job.shops?.name || 'Unknown',
        customer_name: job.customers?.nickname || 'Unknown',
        customers: job.customers, // Changed from 'customer' to 'customers' to match expected key
        inventory_quantity: job.parts?.inventory_quantity || 0,
        parts: job.parts, // Preserve the entire parts object
      }));
      setKanbanJobs(formattedJobs);

      const { data: workOrdersData, error: workOrdersError } = await supabase
        .from('jobs')
        .select(`
          id, kanban_job_id, quantity_on_order, produced_quantity, shipped_quantity, 
          expected_delivery_date, status, po_number, po_line_number,
          kanban_jobs!inner(id, po_number, po_line_number, part_number)
        `)
        .in('kanban_job_id', formattedJobs.map(job => job.id))
        .order('expected_delivery_date', { ascending: true });
      if (workOrdersError) throw workOrdersError;
      setWorkOrders(workOrdersData || []);

      const { data: filesData, error: filesError } = await supabase
        .from('job_files')
        .select('*')
        .eq('po_number', jobData.po_number);
      if (filesError) throw filesError;
      setFiles(filesData || []);

      const { data: deliveriesData, error: deliveriesError } = await supabase
        .from('deliveries')
        .select('delivery_id, package_number, quantity, ship_date, invoice_number, packing_slip_path')
        .in('kanban_job_id', formattedJobs.map(job => job.id));
      if (deliveriesError) throw deliveriesError;
      setDeliveries(deliveriesData || []);
    } catch (err) {
      console.error('Error fetching Kanban job:', err.message);
      toast.error(`Failed to fetch Kanban job: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  fetchKanbanJob();
}, [user, id]);

  const generateKanbanWorkOrderPoNumber = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const chars = [
      letters[Math.floor(Math.random() * letters.length)],
      letters[Math.floor(Math.random() * letters.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
    ];
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return `KAN${chars.join('')}`;
  };

  const getNextPoLineNumber = async () => {
    const { data, error } = await supabase
      .from('jobs')
      .select('po_line_number')
      .in('kanban_job_id', kanbanJobs.map(job => job.id))
      .order('po_line_number', { ascending: false })
      .limit(1);
    if (error) throw new Error(`Failed to fetch work orders: ${error.message}`);
    return data.length > 0 ? parseInt(data[0].po_line_number) + 1 : 1;
  };

  const openCreateWorkOrderModal = () => {
    setNewWorkOrderLineItem(null);
    setNewQuantityOrdered('');
    setNewDueDate('');
    setIsCreateWorkOrderModalOpen(true);
  };

  const closeCreateWorkOrderModal = () => {
    setIsCreateWorkOrderModalOpen(false);
    setNewWorkOrderLineItem(null);
    setNewQuantityOrdered('');
    setNewDueDate('');
  };

  const handleCreateWorkOrder = async () => {
    if (!newWorkOrderLineItem || !newQuantityOrdered || newQuantityOrdered <= 0 || !newDueDate) {
      toast.error('Please fill in all fields with valid values.');
      return;
    }

    try {
      const { data: kanbanJobData, error: fetchKanbanError } = await supabase
        .from('kanban_jobs')
        .select('unit_price')
        .eq('id', newWorkOrderLineItem.id)
        .single();
      if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);

      if (!kanbanJobData || kanbanJobData.unit_price == null) {
        throw new Error('Unit price not found for the selected Kanban job.');
      }

      const kanbanWorkOrderPoNumber = generateKanbanWorkOrderPoNumber();
      const kanbanWorkOrderPoLineNumber = await getNextPoLineNumber();

      const { data, error } = await supabase
        .from('jobs')
        .insert({
          kanban_job_id: newWorkOrderLineItem.id,
          po_number: kanbanWorkOrderPoNumber,
          po_line_number: kanbanWorkOrderPoLineNumber,
          part_number: newWorkOrderLineItem.part_number,
          description: newWorkOrderLineItem.description,
          quantity_on_order: parseInt(newQuantityOrdered),
          produced_quantity: 0,
          shipped_quantity: 0,
          expected_delivery_date: newDueDate,
          status: 'not_started',
          shop_id: newWorkOrderLineItem.shops?.id || 1,
          customer_id: newWorkOrderLineItem.customers?.id || 1,
          unit_price: kanbanJobData.unit_price,
        })
        .select(`
          id, kanban_job_id, quantity_on_order, produced_quantity, shipped_quantity, 
          expected_delivery_date, status, po_number, po_line_number,
          kanban_jobs!inner(id, po_number, po_line_number, part_number)
        `)
        .single();
      if (error) throw new Error(`Failed to create work order: ${error.message}`);

      setWorkOrders([...workOrders, data]);
      closeCreateWorkOrderModal();
      toast.success('Work order created successfully!');
    } catch (err) {
      console.error('Error creating work order:', err);
      toast.error(`Failed to create work order: ${err.message}`);
    }
  };

  const openLogProductionModal = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setProductionQuantity('');
    setIsLogProductionModalOpen(true);
  };

  const closeLogProductionModal = () => {
    setIsLogProductionModalOpen(false);
    setSelectedWorkOrder(null);
    setProductionQuantity('');
  };

  const handleLogProduction = async () => {
    if (!productionQuantity || productionQuantity <= 0) {
      toast.error('Please enter a valid production quantity.');
      return;
    }

    const newProducedQuantity = selectedWorkOrder.produced_quantity + parseInt(productionQuantity);
    if (newProducedQuantity > selectedWorkOrder.quantity_on_order) {
      toast.error('Produced quantity cannot exceed ordered quantity.');
      return;
    }

    try {
      const { error: workOrderError } = await supabase
        .from('jobs')
        .update({
          produced_quantity: newProducedQuantity,
          status: newProducedQuantity === selectedWorkOrder.quantity_on_order ? 'ready_for_shipment' : selectedWorkOrder.status,
        })
        .eq('id', selectedWorkOrder.id);
      if (workOrderError) throw new Error(`Failed to update work order: ${workOrderError.message}`);

      const { data: kanbanJobData, error: fetchKanbanError } = await supabase
        .from('kanban_jobs')
        .select('produced_quantity')
        .eq('id', selectedWorkOrder.kanban_job_id)
        .single();
      if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);

      const newKanbanProducedQuantity = kanbanJobData.produced_quantity + parseInt(productionQuantity);
      const { error: updateKanbanError } = await supabase
        .from('kanban_jobs')
        .update({
          produced_quantity: newKanbanProducedQuantity,
        })
        .eq('id', selectedWorkOrder.kanban_job_id);
      if (updateKanbanError) throw new Error(`Failed to update Kanban job: ${updateKanbanError.message}`);

      setWorkOrders(workOrders.map(wo =>
        wo.id === selectedWorkOrder.id
          ? { ...wo, produced_quantity: newProducedQuantity }
          : wo
      ));
      setKanbanJobs(kanbanJobs.map(job =>
        job.id === selectedWorkOrder.kanban_job_id
          ? { ...job, produced_quantity: newKanbanProducedQuantity }
          : job
      ));

      closeLogProductionModal();
      toast.success('Production logged successfully!');
    } catch (err) {
      console.error('Error logging production:', err);
      toast.error(`Failed to log production: ${err.message}`);
    }
  };

  const handleDeleteKanbanJob = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete Kanban PO #${poNumber} and all its line items? This action cannot be undone.`
    );
    if (!confirmed) return;
  
    try {
      // Delete associated work orders (jobs)
      const { error: workOrdersError } = await supabase
        .from('jobs')
        .delete()
        .in('kanban_job_id', kanbanJobs.map(job => job.id));
      if (workOrdersError) throw new Error(`Failed to delete associated work orders: ${workOrdersError.message}`);
  
      // Delete associated deliveries
      const { error: deliveriesError } = await supabase
        .from('deliveries')
        .delete()
        .in('kanban_job_id', kanbanJobs.map(job => job.id));
      if (deliveriesError) throw new Error(`Failed to delete associated deliveries: ${deliveriesError.message}`);
  
      // Delete all Kanban jobs under this PO
      const { error } = await supabase
        .from('kanban_jobs')
        .delete()
        .eq('po_number', poNumber);
      if (error) throw new Error(`Failed to delete Kanban Jobs: ${error.message}`);
  
      // Redirect to Kanban Jobs list page
      toast.success('Kanban PO deleted successfully!');
      window.location.href = '/kanban'; // Adjust the redirect URL as needed
    } catch (err) {
      console.error('Error deleting Kanban PO:', err.message);
      toast.error(`Failed to delete Kanban PO: ${err.message}`);
    }
  };

  const openEditPoModal = (kanbanJob) => {
    setSelectedKanbanJob(kanbanJob);
    setEditedPoNumber(kanbanJob.po_number);
    setEditedLineItems(kanbanJobs.map(job => ({
      id: job.id,
      part_number: job.part_number,
      description: job.description,
      quantity_on_order: job.quantity_on_order,
      unit_price: job.unit_price || 0,
      shop_id: job.shops?.id || '',
    })));
    setNewLineItem({
      part_number: '',
      description: '',
      quantity_on_order: '',
      unit_price: '',
      shop_id: '',
    });
    setIsEditPoModalOpen(true);
  };
  
  const closeEditPoModal = () => {
    setIsEditPoModalOpen(false);
    setSelectedKanbanJob(null);
    setEditedPoNumber('');
    setEditedLineItems([]);
    setNewLineItem({
      part_number: '',
      description: '',
      quantity_on_order: '',
      shop_id: '',
    });
  };
  
  const handleEditPo = async () => {
    if (
      !editedPoNumber ||
      editedLineItems.some(
        item =>
          !item.part_number ||
          !item.quantity_on_order ||
          item.quantity_on_order <= 0 ||
          (item.unit_price !== undefined && (isNaN(item.unit_price) || item.unit_price < 0))
      )
    ) {
      toast.error('Please fill in all required fields with valid values (unit price must be non-negative).');
      return;
    }
  
    try {
      // Update existing line items
      for (const item of editedLineItems) {
        const { error } = await supabase
          .from('kanban_jobs')
          .update({
            po_number: editedPoNumber,
            part_number: item.part_number,
            description: item.description,
            quantity_on_order: parseInt(item.quantity_on_order),
            unit_price: parseFloat(item.unit_price) || 0,
            shop_id: item.shop_id || null,
          })
          .eq('id', item.id);
        if (error) throw new Error(`Failed to update Kanban Job ${item.id}: ${error.message}`);
      }
  
      // Add new line item if it exists
      if (newLineItem.part_number && newLineItem.quantity_on_order) {
        if (isNaN(newLineItem.unit_price) || newLineItem.unit_price < 0) {
          toast.error('Unit price for new line item must be a non-negative number.');
          return;
        }
        const { data, error } = await supabase
          .from('kanban_jobs')
          .insert({
            po_number: editedPoNumber,
            po_line_number: kanbanJobs.length + 1,
            part_number: newLineItem.part_number,
            description: newLineItem.description,
            quantity_on_order: parseInt(newLineItem.quantity_on_order),
            unit_price: parseFloat(newLineItem.unit_price) || 0,
            produced_quantity: 0,
            shipped_quantity: 0,
            po_start_date: new Date().toISOString(),
            status: 'not_started',
            shop_id: newLineItem.shop_id || null,
            customer_id: selectedKanbanJob.customer?.id || 1,
          })
          .select(`
            id, po_number, po_line_number, part_number, description, status, 
            quantity_on_order, produced_quantity, shipped_quantity, 
            po_start_date, unit_price, 
            shops(id, name), customers(id, nickname, name, 
              billing_address, billing_city, billing_state, billing_zip, 
              billing_contact_name, billing_contact_email, billing_contact_phone,
              shipping_address, shipping_city, shipping_state, shipping_zip,
              shipping_contact_name, shipping_contact_email, shipping_contact_phone, net_terms),
            parts!inner(part_number, inventory_quantity, part_number_master, revision)
          `)
          .single();
        if (error) throw new Error(`Failed to add new Kanban Job: ${error.message}`);
  
        setKanbanJobs([...kanbanJobs, {
          id: data.id,
          po_number: data.po_number,
          po_line_number: data.po_line_number,
          part_number: data.part_number,
          description: data.description,
          status: data.status,
          quantity_on_order: data.quantity_on_order,
          unit_price: data.unit_price || 0,
          produced_quantity: data.produced_quantity,
          shipped_quantity: data.shipped_quantity,
          po_start_date: data.po_start_date,
          shop_name: data.shops?.name || 'Unknown',
          customer_name: data.customers?.nickname || 'Unknown',
          customer: data.customers,
          inventory_quantity: data.parts?.inventory_quantity || 0,
        }]);
      }
  
      // Refresh Kanban jobs to ensure consistency
      const { data: refreshedKanbanJobs, error: fetchError } = await supabase
        .from('kanban_jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status, 
          quantity_on_order, produced_quantity, shipped_quantity, 
          po_start_date, unit_price, 
          shops(id, name), customers(id, nickname, name, 
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone, net_terms),
          parts!inner(part_number, inventory_quantity, part_number_master, revision)
        `)
        .eq('po_number', editedPoNumber)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated Kanban jobs: ${fetchError.message}`);
  
      setKanbanJobs(refreshedKanbanJobs.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        po_start_date: job.po_start_date,
        unit_price: job.unit_price || 0,
        shop_name: job.shops?.name || 'Unknown',
        customer_name: job.customers?.nickname || 'Unknown',
        customer: job.customers,
        inventory_quantity: job.parts?.inventory_quantity || 0,
        parts: job.parts, // Preserve the entire parts object
      })) || []);
  
      setPoNumber(editedPoNumber);
      closeEditPoModal();
      toast.success('PO updated successfully!');
    } catch (err) {
      console.error('Error updating PO:', err);
      toast.error(`Failed to update PO: ${err.message}`);
    }
  };

  const openLogShipmentModal = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShipmentQuantity('');
    setIsLogShipmentModalOpen(true);
  };

  const closeLogShipmentModal = () => {
    setIsLogShipmentModalOpen(false);
    setSelectedWorkOrder(null);
    setShipmentQuantity('');
  };

  const handleLogShipment = async () => {
    if (!shipmentQuantity || shipmentQuantity <= 0) {
      toast.error('Please enter a valid shipment quantity.');
      return;
    }

    const newShippedQuantity = selectedWorkOrder.shipped_quantity + parseInt(shipmentQuantity);
    if (newShippedQuantity > selectedWorkOrder.quantity_on_order) {
      toast.error('Shipped quantity cannot exceed ordered quantity.');
      return;
    }
    if (newShippedQuantity > selectedWorkOrder.produced_quantity) {
      toast.error('Shipped quantity cannot exceed produced quantity.');
      return;
    }

    try {
      const { error: workOrderError } = await supabase
        .from('jobs')
        .update({
          shipped_quantity: newShippedQuantity,
          status: newShippedQuantity === selectedWorkOrder.quantity_on_order ? 'closed' : 'partially_shipped',
        })
        .eq('id', selectedWorkOrder.id);
      if (workOrderError) throw new Error(`Failed to update work order: ${workOrderError.message}`);

      const { data: kanbanJobData, error: fetchKanbanError } = await supabase
        .from('kanban_jobs')
        .select('shipped_quantity')
        .eq('id', selectedWorkOrder.kanban_job_id)
        .single();
      if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);

      const newKanbanShippedQuantity = kanbanJobData.shipped_quantity + parseInt(shipmentQuantity);
      const { error: updateKanbanError } = await supabase
        .from('kanban_jobs')
        .update({
          shipped_quantity: newKanbanShippedQuantity,
        })
        .eq('id', selectedWorkOrder.kanban_job_id);
      if (updateKanbanError) throw new Error(`Failed to update Kanban job: ${updateKanbanError.message}`);

      setWorkOrders(workOrders.map(wo =>
        wo.id === selectedWorkOrder.id
          ? { ...wo, shipped_quantity: newShippedQuantity }
          : wo
      ));
      setKanbanJobs(kanbanJobs.map(job =>
        job.id === selectedWorkOrder.kanban_job_id
          ? { ...job, shipped_quantity: newKanbanShippedQuantity }
          : job
      ));

      closeLogShipmentModal();
      toast.success('Shipment logged successfully!');
    } catch (err) {
      console.error('Error logging shipment:', err);
      toast.error(`Failed to log shipment: ${err.message}`);
    }
  };


  const closeCreateDeliveryModal = () => {
    setIsCreateDeliveryModalOpen(false);
    setSelectedWorkOrder(null);
    setDeliveryQuantity('');
    setPackageNumber('');
    setShipDate('');
    setInvoiceNumber('');
  };

  const handleCreateDelivery = async () => {
    if (!deliveryQuantity || deliveryQuantity <= 0 || !packageNumber || !shipDate) {
      toast.error('Please fill in all required fields with valid values.');
      return;
    }

    const deliveryQty = parseInt(deliveryQuantity);
    if (deliveryQty > selectedWorkOrder.shipped_quantity) {
      toast.error('Delivered quantity cannot exceed shipped quantity.');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('deliveries')
        .insert({
          kanban_job_id: selectedWorkOrder.kanban_job_id,
          package_number: packageNumber,
          quantity: deliveryQty,
          ship_date: shipDate,
          invoice_number: invoiceNumber || null,
        })
        .select()
        .single();
      if (error) throw new Error(`Failed to create delivery: ${error.message}`);

      setDeliveries([...deliveries, data]);
      closeCreateDeliveryModal();
      toast.success('Delivery created successfully!');
    } catch (err) {
      console.error('Error creating delivery:', err);
      toast.error(`Failed to create delivery: ${err.message}`);
    }
  };

  const openUploadModal = () => {
    setFileToUpload(null);
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setFileToUpload(null);
  };

  const handleFileUpload = async () => {
    if (!fileToUpload) {
      toast.error('Please select a file to upload.');
      return;
    }
  
    try {
      // Upload the file to the job-files storage bucket
      const fileName = `${poNumber}/${Date.now()}_${fileToUpload.name}`;
      const { error: uploadError } = await supabase
        .storage
        .from('job-files')
        .upload(fileName, fileToUpload);
      if (uploadError) throw new Error(`Failed to upload file: ${uploadError.message}`);
  
      // Insert a record into the job_files table
      const { error: insertError } = await supabase
        .from('job_files')
        .insert({
          po_number: poNumber,
          file_name: fileToUpload.name,
          file_path: fileName,
          file_type: 'Other', // You can determine file_type based on file extension if needed
          uploaded_by: user.id,
          uploaded_at: new Date().toISOString(),
        });
      if (insertError) throw new Error(`Failed to insert file record: ${insertError.message}`);
  
      // Fetch the updated list of files for this PO
      const { data: filesData, error: filesError } = await supabase
        .from('job_files')
        .select('*')
        .eq('po_number', poNumber);
      if (filesError) throw filesError;
  
      setFiles(filesData || []);
      closeUploadModal();
      toast.success('File uploaded successfully!');
    } catch (err) {
      console.error('Error uploading file:', err);
      toast.error(`Failed to upload file: ${err.message}`);
    }
  };

  const handleFileDownload = async (filePath) => {
    try {
      const { data, error } = await supabase
        .storage
        .from('job-files')
        .createSignedUrl(filePath, 60);
      if (error) throw new Error(`Failed to generate signed URL: ${error.message}`);
  
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error(`Failed to download file: ${err.message}`);
    }
  };

  const refreshKanbanJobs = async () => {
    try {
      const { data: refreshedKanbanJobs, error: fetchError } = await supabase
        .from('kanban_jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status, 
          quantity_on_order, produced_quantity, shipped_quantity, 
          po_start_date, unit_price, 
          shops(id, name), customers(id, nickname, name, 
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone, net_terms),
          parts!inner(part_number, inventory_quantity, part_number_master, revision)
        `)
        .eq('po_number', poNumber)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated Kanban jobs: ${fetchError.message}`);
  
      setKanbanJobs(refreshedKanbanJobs.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        part_number_master: job.parts?.part_number_master || job.part_number,
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        po_start_date: job.po_start_date,
        unit_price: job.unit_price || 0,
        shop_name: job.shops?.name || 'Unknown',
        customer_name: job.customers?.nickname || 'Unknown',
        customers: job.customers, // Ensure this matches the expected key
        inventory_quantity: job.parts?.inventory_quantity || 0,
      })) || []);
    } catch (err) {
      console.error('Error refreshing kanban jobs:', err);
      toast.error(`Failed to refresh kanban jobs: ${err.message}`);
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;

  if (!poNumber) return <div className="text-center text-red-500 py-10">Kanban job not found.</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Kanban PO #{poNumber}
          </h1>
          <Menu as="div" className="relative inline-block text-left">
  <div>
    <Menu.Button className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
      Actions
      <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
    </Menu.Button>
  </div>
  <Transition
    as={Fragment}
    enter="transition ease-out duration-100"
    enterFrom="transform opacity-0 scale-95"
    enterTo="transform opacity-100 scale-100"
    leave="transition ease-in duration-75"
    leaveFrom="transform opacity-100 scale-100"
    leaveTo="transform opacity-0 scale-95"
  >
    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
      <div className="py-1">
        <Menu.Item>
          {({ active }) => (
            <button
              onClick={openCreateWorkOrderModal}
              className={`${
                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
              } block px-4 py-2 text-sm w-full text-left transition duration-150`}
            >
              Create Work Order
            </button>
          )}
        </Menu.Item>
        <Menu.Item>
          {({ active }) => (
            <button
            onClick={() => setIsNewDeliveryModalOpen(true)}
              className={`${
                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
              } block px-4 py-2 text-sm w-full text-left transition duration-150`}
            >
              New Delivery
            </button>
          )}
        </Menu.Item>
        <Menu.Item>
          {({ active }) => (
            <button
              onClick={() => kanbanJobs.length > 0 && openEditPoModal(kanbanJobs[0])}
              className={`${
                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
              } group flex items-center w-full px-4 py-2 text-sm transition duration-150`}
            >
              <PencilIcon className="w-5 h-5 mr-3 text-gray-500" />
              Edit PO
            </button>
          )}
        </Menu.Item>
        <Menu.Item>
          {({ active }) => (
            <button
              onClick={() => handleDeleteKanbanJob()}
              className={`${
                active ? 'bg-gray-100 text-red-900' : 'text-red-700'
              } group flex items-center w-full px-4 py-2 text-sm transition duration-150`}
            >
              <TrashIcon className="w-5 h-5 mr-3 text-red-500" />
              Delete PO
            </button>
          )}
        </Menu.Item>
      </div>
    </Menu.Items>
  </Transition>
</Menu>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Customer</p>
              <p className="text-lg text-gray-900">{kanbanJobs[0]?.customer_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Shop</p>
              <p className="text-lg text-gray-900">{kanbanJobs[0]?.shop_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Total Line Items</p>
              <p className="text-lg text-gray-900">{kanbanJobs.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
  <h2 className="text-xl font-semibold text-gray-900 mb-4">Overall Progress</h2>
  <div className="space-y-6">
    {/* Overall Progress Wheel */}
    <div>
      <h3 className="text-lg font-medium text-gray-700 mb-2">Overall Shipment Progress</h3>
      <div className="flex items-center space-x-4">
        <div className="relative w-24 h-24">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            {/* Background Circle */}
            <circle
              cx="50"
              cy="50"
              r="40"
              stroke="#e5e7eb"
              strokeWidth="10"
              fill="none"
            />
            {/* Progress Circle */}
            <circle
              cx="50"
              cy="50"
              r="40"
              stroke="#16a34a" // Ensure the color is applied directly
              strokeWidth="10"
              fill="none"
              strokeDasharray="251.2"
              strokeDashoffset={
                251.2 -
                (251.2 *
                  (kanbanJobs.reduce((sum, job) => sum + job.shipped_quantity, 0) /
                    kanbanJobs.reduce((sum, job) => sum + job.quantity_on_order, 0))) 
              }
              transform="rotate(-90 50 50)"
              strokeLinecap="round" // Add stroke-linecap for smoother ends
            />
            {/* Percentage Text */}
            <text
              x="50"
              y="50"
              textAnchor="middle"
              dy=".3em"
              className="text-lg font-semibold text-gray-900"
            >
              {Math.round(
                (kanbanJobs.reduce((sum, job) => sum + job.shipped_quantity, 0) /
                  kanbanJobs.reduce((sum, job) => sum + job.quantity_on_order, 0)) * 100
              ) || 0}%
            </text>
          </svg>
        </div>
        <p className="text-sm text-gray-600">
          {kanbanJobs.reduce((sum, job) => sum + job.shipped_quantity, 0)} /{' '}
          {kanbanJobs.reduce((sum, job) => sum + job.quantity_on_order, 0)} shipped
        </p>
      </div>
    </div>

    {/* Individual Line Item Progress Wheels */}
    <div>
      <h3 className="text-lg font-medium text-gray-700 mb-4">Shipment Progress by Line Item</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {kanbanJobs.map((job) => (
          <div key={job.id} className="flex items-center space-x-4">
            <div className="relative w-16 h-16">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                {/* Background Circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke="#e5e7eb"
                  strokeWidth="10"
                  fill="none"
                />
                {/* Progress Circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke="#16a34a" // Ensure the color is applied directly
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray="251.2"
                  strokeDashoffset={
                    251.2 -
                    (251.2 * (job.shipped_quantity / job.quantity_on_order))
                  }
                  transform="rotate(-90 50 50)"
                  strokeLinecap="round" // Add stroke-linecap for smoother ends
                />
                {/* Percentage Text */}
                <text
                  x="50"
                  y="50"
                  textAnchor="middle"
                  dy=".3em"
                  className="text-lg font-semibold text-gray-900"
                >
                  {Math.round((job.shipped_quantity / job.quantity_on_order) * 100) || 0}%
                </text>
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                Line {job.po_line_number}: {job.part_number}
              </p>
              <p className="text-sm text-gray-600">
                {job.shipped_quantity} / {job.quantity_on_order} shipped
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
</div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
  <h2 className="text-xl font-semibold text-gray-900 mb-4">Line Items</h2>
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line #</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">QTY</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inventory</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {kanbanJobs.map((job) => (
          <tr key={job.id} className="hover:bg-gray-50 transition duration-150">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.po_line_number}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.part_number}</td>
            <td className="px-6 py-4 text-sm text-gray-900">{job.description}</td>
            <td className="px-6 py-4 text-sm text-gray-900">${job.unit_price.toFixed(2)}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.quantity_on_order}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.inventory_quantity}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Work Orders</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Work ID</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Line #</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Part Number</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">QTY</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Produced</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workOrders.map((workOrder) => (
                  <tr key={workOrder.id} className="hover:bg-gray-50 transition duration-150">
<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
  <Link
    to={`/jobs/${workOrder.po_number}`}
    className="text-indigo-600 hover:text-indigo-800 transition duration-150"
  >
    {`${workOrder.po_number}-${workOrder.po_line_number}`}
  </Link>
</td>                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.kanban_jobs.po_line_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.kanban_jobs.part_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.quantity_on_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.produced_quantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(workOrder.expected_delivery_date).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{workOrder.status.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
  <div className="flex justify-between items-center mb-4">
    <h2 className="text-xl font-semibold text-gray-900">Files</h2>
    <button
      onClick={openUploadModal}
      className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
    >
      <PlusIcon className="w-5 h-5 mr-2" />
      Upload File
    </button>
  </div>
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">File Name</th>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">File Type</th>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Uploaded At</th>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {files.map((file) => (
          <tr key={file.id} className="hover:bg-gray-50 transition duration-150">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{file.file_name}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{file.file_type}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(file.uploaded_at).toLocaleString()}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm">
              <button
                onClick={() => handleFileDownload(file.file_path)}
                className="text-indigo-600 hover:text-indigo-800 transition duration-150 flex items-center space-x-1"
              >
                <DocumentArrowDownIcon className="w-5 h-5" />
                <span>Preview</span>
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

<div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
  <h2 className="text-xl font-semibold text-gray-900 mb-4">Deliveries</h2>
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Package #</th>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ship Date</th>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {deliveries.map((delivery) => (
          <tr key={delivery.delivery_id} className="hover:bg-gray-50 transition duration-150">
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{delivery.package_number}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.quantity}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(delivery.ship_date).toLocaleDateString()}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm">
              {delivery.invoice_number ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Invoiced: {delivery.invoice_number}
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Uninvoiced
                </span>
              )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm">
              {delivery.packing_slip_path && (
                <button
                  onClick={() => handlePreviewPDF(delivery.packing_slip_path, 'delivery-files')}
                  className="text-indigo-600 hover:text-indigo-800 transition duration-150 flex items-center space-x-1"
                >
                  <DocumentArrowDownIcon className="w-5 h-5" />
                  <span>Preview</span>
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

        <Transition appear show={isCreateWorkOrderModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeCreateWorkOrderModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Create Work Order for PO #{poNumber}
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Line Item</label>
                        <select
                          value={newWorkOrderLineItem?.id || ''}
                          onChange={(e) => {
                            const selectedJob = kanbanJobs.find(job => job.id === parseInt(e.target.value));
                            setNewWorkOrderLineItem(selectedJob);
                          }}
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        >
                          <option value="">Select a line item</option>
                          {kanbanJobs.map((job) => (
                            <option key={job.id} value={job.id}>
                              Line {job.po_line_number}: {job.part_number}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Ordered</label>
                        <input
                          type="number"
                          value={newQuantityOrdered}
                          onChange={(e) => setNewQuantityOrdered(e.target.value)}
                          placeholder="Enter quantity ordered"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          value={newDueDate}
                          onChange={(e) => setNewDueDate(e.target.value)}
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeCreateWorkOrderModal}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateWorkOrder}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        Create
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        <Transition appear show={isLogProductionModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeLogProductionModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Log Production for Work Order {selectedWorkOrder?.po_number}-{selectedWorkOrder?.po_line_number} (Line {selectedWorkOrder?.kanban_jobs.po_line_number})
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Produced</label>
                        <input
                          type="number"
                          value={productionQuantity}
                          onChange={(e) => setProductionQuantity(e.target.value)}
                          placeholder="Enter quantity produced"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeLogProductionModal}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleLogProduction}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        Log Production
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        <Transition appear show={isLogShipmentModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeLogShipmentModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Log Shipment for Work Order {selectedWorkOrder?.po_number}-{selectedWorkOrder?.po_line_number} (Line {selectedWorkOrder?.kanban_jobs.po_line_number})
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Shipped</label>
                        <input
                          type="number"
                          value={shipmentQuantity}
                          onChange={(e) => setShipmentQuantity(e.target.value)}
                          placeholder="Enter quantity shipped"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeLogShipmentModal}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleLogShipment}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        Log Shipment
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

       

        <Transition appear show={isUploadModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeUploadModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Upload File
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select File</label>
                        <input
                          type="file"
                          onChange={(e) => setFileToUpload(e.target.files[0])}
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeUploadModal}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleFileUpload}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
                      >
                        Upload
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        <Transition appear show={isPdfPreviewModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={closePdfPreviewModal}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
              Packing Slip Preview
            </Dialog.Title>
            <div className="mb-6">
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  title="Packing Slip Preview"
                  className="w-full h-[600px] border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-500">Loading PDF...</p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closePdfPreviewModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>

<Transition appear show={isEditPoModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={closeEditPoModal}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
              Edit PO #{editedPoNumber}
            </Dialog.Title>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                <input
                  type="text"
                  value={editedPoNumber}
                  onChange={(e) => setEditedPoNumber(e.target.value)}
                  className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Line Items</h3>
                {editedLineItems.map((item, index) => (
                  <div key={item.id} className="flex space-x-4 mb-4 items-center">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
                      <input
                        type="text"
                        value={item.part_number}
                        onChange={(e) => {
                          const updatedItems = [...editedLineItems];
                          updatedItems[index] = { ...updatedItems[index], part_number: e.target.value };
                          setEditedLineItems(updatedItems);
                        }}
                        className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => {
                          const updatedItems = [...editedLineItems];
                          updatedItems[index] = { ...updatedItems[index], description: e.target.value };
                          setEditedLineItems(updatedItems);
                        }}
                        className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                      <input
                        type="number"
                        value={item.quantity_on_order}
                        onChange={(e) => {
                          const updatedItems = [...editedLineItems];
                          updatedItems[index] = { ...updatedItems[index], quantity_on_order: e.target.value };
                          setEditedLineItems(updatedItems);
                        }}
                        className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        min="1"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                      <input
                        type="number"
                        value={item.unit_price || ''}
                        onChange={(e) => {
                          const updatedItems = [...editedLineItems];
                          updatedItems[index] = { ...updatedItems[index], unit_price: e.target.value };
                          setEditedLineItems(updatedItems);
                        }}
                        className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Shop</label>
                      <select
                        value={item.shop_id || ''}
                        onChange={(e) => {
                          const updatedItems = [...editedLineItems];
                          updatedItems[index] = { ...updatedItems[index], shop_id: e.target.value };
                          setEditedLineItems(updatedItems);
                        }}
                        className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      >
                        <option value="">Select a shop</option>
                        {shops.map((shop) => (
                          <option key={shop.id} value={shop.id}>
                            {shop.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
                <h3 className="text-lg font-medium text-gray-900 mb-2">Add New Line Item</h3>
                <div className="flex space-x-4 mb-4 items-center">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
                    <input
                      type="text"
                      value={newLineItem.part_number}
                      onChange={(e) => setNewLineItem({ ...newLineItem, part_number: e.target.value })}
                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={newLineItem.description}
                      onChange={(e) => setNewLineItem({ ...newLineItem, description: e.target.value })}
                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      value={newLineItem.quantity_on_order}
                      onChange={(e) => setNewLineItem({ ...newLineItem, quantity_on_order: e.target.value })}
                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      min="1"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                    <input
                      type="number"
                      value={newLineItem.unit_price || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, unit_price: e.target.value })}
                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shop</label>
                    <select
                      value={newLineItem.shop_id}
                      onChange={(e) => setNewLineItem({ ...newLineItem, shop_id: e.target.value })}
                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                    >
                      <option value="">Select a shop</option>
                      {shops.map((shop) => (
                        <option key={shop.id} value={shop.id}>
                          {shop.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeEditPoModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleEditPo}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
              >
                Save Changes
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>
      </div>

      <CreateDelivery
  isOpen={isNewDeliveryModalOpen}
  onClose={() => setIsNewDeliveryModalOpen(false)}
  jobsData={kanbanJobs}
  poNumber={poNumber}
  deliveriesData={deliveries}
  setDeliveriesData={setDeliveries}
  isKanban={true}
  usePartNumberMaster={true} // KanbanJob uses part_number_master in packing slip
  onDeliveryCreated={refreshKanbanJobs}
/>

    </div>
  );
}

export default KanbanJob;